/**
 * Queue bottom sheet — Pending / History tabs.
 * Pending: shows items awaiting sync with accordion expand + edit/delete per row.
 * History: shows successfully synced items with timestamps.
 */

import { getAll, remove, getHistory, clearHistory, pendingCount } from '../db.js';
import { syncAll, isSyncing } from '../sync.js';
import { isConfigured } from '../settings.js';
import { showToast } from '../toast.js';
import { hapticTap } from '../haptics.js';
import { router } from '../router.js';

const TYPE_LABELS = {
  create_asset: 'Add Asset',
  checkout: 'Check Out',
  checkin: 'Check In',
  update_asset: 'Edit Asset',
};

const STATUS_CLASSES = {
  pending: '',
  syncing: 'pending',
  done: 'deployable',
  error: 'archived',
};

// Friendly payload field labels for accordion / history detail
const PAYLOAD_LABELS = {
  name: 'Name',
  asset_tag: 'Asset Tag',
  serial: 'Serial',
  model_name: 'Model',
  notes: 'Notes',
  _snipeit_wlan_mac_address_6: 'WLAN MAC',
  _snipeit_mac_address_1: 'LAN MAC',
  _snipeit_local_ip_address_7: 'IP Address',
  // Checkout / checkin
  checkout_to_type: 'To Type',
  asset_name: 'Asset',
  target_name: 'Target',
};

let sheetOpen = false;
let activeTab = 'pending';

/**
 * Initialize the queue sheet and FAB. Call once on app startup.
 */
export function initQueueSheet() {
  renderFAB();
  renderSheet();

  window.addEventListener('open-queue', (e) => {
    const tab = e.detail?.tab || 'pending';
    openSheet(tab);
  });
  window.addEventListener('queue-updated', () => {
    updateFABBadge();
    if (sheetOpen) refreshSheetBody();
  });

  updateFABBadge();
}

function renderFAB() {
  const existing = document.getElementById('queue-fab');
  if (existing) existing.remove();

  const btn = document.createElement('button');
  btn.id = 'queue-fab';
  btn.className = 'fab';
  btn.setAttribute('aria-label', 'Open queue');
  btn.innerHTML = `
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/>
      <path d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z"/>
    </svg>
    <span class="queue-badge hidden" id="queue-fab-badge">0</span>
  `;
  btn.addEventListener('click', () => {
    hapticTap();
    openSheet('pending');
  });
  document.body.appendChild(btn);
}

function renderSheet() {
  const container = document.getElementById('queue-sheet-container');
  container.innerHTML = `
    <div class="bottom-sheet-backdrop" id="queue-backdrop"></div>
    <div class="bottom-sheet" id="queue-sheet">
      <div class="sheet-handle"></div>
      <div class="sheet-header" style="flex-direction:column; gap:8px; align-items:stretch;">
        <div style="display:flex; justify-content:space-between; align-items:center;">
          <h2>Queue</h2>
          <button class="btn btn-secondary btn-sm" id="queue-close-btn">Close</button>
        </div>
        <div class="queue-tabs" role="tablist">
          <button class="queue-tab active" id="tab-pending" role="tab" aria-selected="true">
            Pending <span class="queue-badge" id="tab-pending-badge" style="background:var(--color-text-muted);">0</span>
          </button>
          <button class="queue-tab" id="tab-history" role="tab" aria-selected="false">History</button>
        </div>
      </div>
      <div class="sheet-body" id="queue-body">
        <p style="text-align:center; padding:24px; color:var(--color-text-muted);">No items in queue</p>
      </div>
      <div class="sheet-footer" id="queue-footer">
        <button class="btn btn-primary btn-sm" id="queue-sync-btn" style="flex:1;">Sync All</button>
      </div>
    </div>
  `;

  document.getElementById('queue-backdrop').addEventListener('click', closeSheet);
  document.getElementById('queue-close-btn').addEventListener('click', closeSheet);

  document.getElementById('tab-pending').addEventListener('click', () => switchTab('pending'));
  document.getElementById('tab-history').addEventListener('click', () => switchTab('history'));

  document.getElementById('queue-sync-btn').addEventListener('click', async () => {
    if (!isConfigured()) {
      showToast('Configure server in Settings first');
      return;
    }
    if (isSyncing()) {
      showToast('Sync already in progress');
      return;
    }
    hapticTap();
    const btn = document.getElementById('queue-sync-btn');
    btn.disabled = true;
    btn.innerHTML = '<span class="spinner"></span> Syncing...';
    await syncAll();
    btn.disabled = false;
    btn.textContent = 'Sync All';
    refreshSheetBody();
  });
}

function switchTab(tab) {
  activeTab = tab;
  document.getElementById('tab-pending').classList.toggle('active', tab === 'pending');
  document.getElementById('tab-pending').setAttribute('aria-selected', tab === 'pending');
  document.getElementById('tab-history').classList.toggle('active', tab === 'history');
  document.getElementById('tab-history').setAttribute('aria-selected', tab === 'history');

  const footer = document.getElementById('queue-footer');
  if (tab === 'history') {
    footer.innerHTML = `<button class="btn btn-secondary btn-sm" id="queue-clear-history-btn" style="flex:1;">Clear History</button>`;
    document.getElementById('queue-clear-history-btn').addEventListener('click', async () => {
      await clearHistory();
      showToast('History cleared');
      refreshSheetBody();
    });
  } else {
    footer.innerHTML = `<button class="btn btn-primary btn-sm" id="queue-sync-btn" style="flex:1;">Sync All</button>`;
    document.getElementById('queue-sync-btn').addEventListener('click', async () => {
      if (!isConfigured()) { showToast('Configure server in Settings first'); return; }
      if (isSyncing()) { showToast('Sync already in progress'); return; }
      hapticTap();
      const btn = document.getElementById('queue-sync-btn');
      btn.disabled = true;
      btn.innerHTML = '<span class="spinner"></span> Syncing...';
      await syncAll();
      btn.disabled = false;
      btn.textContent = 'Sync All';
      refreshSheetBody();
    });
  }

  refreshSheetBody();
}

function openSheet(tab = 'pending') {
  sheetOpen = true;
  document.getElementById('queue-backdrop').classList.add('open');
  document.getElementById('queue-sheet').classList.add('open');
  switchTab(tab);
}

function closeSheet() {
  sheetOpen = false;
  document.getElementById('queue-backdrop').classList.remove('open');
  document.getElementById('queue-sheet').classList.remove('open');
}

async function refreshSheetBody() {
  const body = document.getElementById('queue-body');
  if (!body) return;

  if (activeTab === 'history') {
    await renderHistoryTab(body);
  } else {
    await renderPendingTab(body);
  }

  await updateFABBadge();
}

async function renderPendingTab(body) {
  const items = await getAll();

  // Update pending badge
  const pendingItems = items.filter((i) => i.status === 'pending');
  const badge = document.getElementById('tab-pending-badge');
  if (badge) {
    badge.textContent = pendingItems.length;
    badge.classList.toggle('hidden', pendingItems.length === 0);
  }

  if (items.length === 0) {
    body.innerHTML = `<p style="text-align:center; padding:24px; color:var(--color-text-muted);">No items in queue</p>`;
    return;
  }

  body.innerHTML = items.map((item) => {
    const isPending = item.status === 'pending';
    return `
      <div class="queue-item" data-id="${item.id}">
        <label class="queue-check-label" aria-label="Include in sync">
          <input type="checkbox" class="queue-check" data-id="${item.id}" ${isPending ? 'checked' : ''} />
        </label>
        <div class="queue-item-info queue-item-expand" data-id="${item.id}" style="cursor:pointer;">
          <div class="queue-item-title">
            ${escapeHtml(TYPE_LABELS[item.type] || item.type)}
            <span class="queue-item-chevron" aria-hidden="true">&#9658;</span>
          </div>
          <div class="queue-item-sub">
            ${summarizePayload(item)} &middot;
            <span class="status-tag ${STATUS_CLASSES[item.status] || ''}">${item.status}</span>
            ${item.error ? `<br><span style="color:var(--color-danger); font-size:0.75rem;">${escapeHtml(item.error)}</span>` : ''}
          </div>
          <div class="queue-item-detail hidden">
            ${renderPayloadDetail(item.payload)}
          </div>
        </div>
        <div style="display:flex; gap:4px; flex-shrink:0;">
          ${isPending ? `<button class="btn btn-secondary btn-sm queue-edit-btn" data-id="${item.id}" style="padding:0 10px;" aria-label="Edit">Edit</button>` : ''}
          <button class="btn btn-secondary btn-sm queue-delete-btn" data-id="${item.id}" aria-label="Delete" style="padding:0 10px;">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
          </button>
        </div>
      </div>
    `;
  }).join('');

  // Accordion toggle
  body.querySelectorAll('.queue-item-expand').forEach((el) => {
    el.addEventListener('click', (e) => {
      // Don't toggle when clicking a child button
      if (e.target.closest('button, input')) return;
      const detail = el.querySelector('.queue-item-detail');
      const chevron = el.querySelector('.queue-item-chevron');
      if (!detail) return;
      const expanded = !detail.classList.contains('hidden');
      detail.classList.toggle('hidden', expanded);
      if (chevron) chevron.style.transform = expanded ? '' : 'rotate(90deg)';
    });
  });

  // Edit handlers
  body.querySelectorAll('.queue-edit-btn').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      closeSheet();
      router.navigate('/queue-edit/' + btn.dataset.id);
    });
  });

  // Delete handlers
  body.querySelectorAll('.queue-delete-btn').forEach((btn) => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const id = parseInt(btn.dataset.id, 10);
      await remove(id);
      window.dispatchEvent(new CustomEvent('queue-updated'));
      refreshSheetBody();
    });
  });
}

async function renderHistoryTab(body) {
  let items;
  try {
    items = await getHistory();
  } catch (err) {
    body.innerHTML = `
      <div style="text-align:center; padding:24px; color:var(--color-text-muted);">
        <p style="font-size:0.875rem; margin-bottom:12px;">Could not load history.<br>Try reloading the app.</p>
        <button class="btn btn-secondary btn-sm" onclick="location.reload()">Reload</button>
      </div>
    `;
    return;
  }

  if (items.length === 0) {
    body.innerHTML = `
      <div style="text-align:center; padding:32px 16px; color:var(--color-text-muted);">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="margin-bottom:10px; opacity:0.4;"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
        <p style="font-size:0.875rem;">Synced items will appear here.</p>
      </div>
    `;
    return;
  }

  body.innerHTML = items.map((item) => `
    <div class="history-item" data-type="${item.type}">
      <div class="history-item-info">
        <div class="history-item-header">
          <span class="history-item-title">${escapeHtml(TYPE_LABELS[item.type] || item.type)}</span>
          <span class="history-item-time">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" style="color:var(--color-success-text);"><polyline points="20 6 9 17 4 12"/></svg>
            ${formatTime(item.syncedAt)}
          </span>
        </div>
        <div class="history-item-sub">${summarizePayload(item)}</div>
      </div>
    </div>
  `).join('');
}

function renderPayloadDetail(payload) {
  if (!payload) return '';
  const entries = Object.entries(payload).filter(([k, v]) => {
    if (!v) return false;
    // Skip internal IDs and unknown technical keys not in PAYLOAD_LABELS
    if (k === 'model_id' || k === 'status_id' || k === 'fieldset_id' || k === 'asset_id') return false;
    return true;
  });
  if (!entries.length) return '';
  return `
    <dl class="queue-summary-dl" style="margin-top:8px;">
      ${entries.map(([k, v]) => `
        <dt>${escapeHtml(PAYLOAD_LABELS[k] || k)}</dt>
        <dd class="${isMacOrTag(k) ? 'mono' : ''}">${escapeHtml(String(v))}</dd>
      `).join('')}
    </dl>
  `;
}

function isMacOrTag(key) {
  return key.includes('mac') || key === 'asset_tag' || key === 'serial' || key.includes('ip');
}

function summarizePayload(item) {
  const p = item.payload || {};
  switch (item.type) {
    case 'create_asset': {
      const parts = [];
      if (p.name) parts.push(escapeHtml(p.name));
      if (p.model_name) parts.push(escapeHtml(p.model_name));
      if (p.serial) parts.push(`SN: ${escapeHtml(p.serial)}`);
      if (p.asset_tag) parts.push(escapeHtml(p.asset_tag));
      return parts.join(' &middot; ') || 'New asset';
    }
    case 'checkout': {
      const assetLabel = p.asset_name ? `"${escapeHtml(p.asset_name)}"` : `#${p.asset_id}`;
      const targetLabel = p.target_name
        ? `"${escapeHtml(p.target_name)}"`
        : `#${p.assigned_user || p.assigned_location || p.assigned_asset || '?'}`;
      return `Asset ${assetLabel} \u2192 ${escapeHtml(p.checkout_to_type || '')} ${targetLabel}`;
    }
    case 'checkin': {
      const assetLabel = p.asset_name ? `"${escapeHtml(p.asset_name)}"` : `#${p.asset_id}`;
      return `Check in ${assetLabel}`;
    }
    case 'update_asset':
      return escapeHtml(`Update "${p.name || `Asset #${p.asset_id}`}"`);
    default:
      return 'Unknown';
  }
}

function formatTime(ts) {
  if (!ts) return '';
  const now = Date.now();
  const diff = now - ts;
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;

  return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

async function updateFABBadge() {
  const count = await pendingCount();
  const badge = document.getElementById('queue-fab-badge');
  if (!badge) return;
  badge.textContent = count;
  badge.classList.toggle('hidden', count === 0);
}

function escapeHtml(str) {
  const div = document.createElement('div');
  div.textContent = String(str ?? '');
  return div.innerHTML;
}
